import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { useSelector, useDispatch } from "react-redux";
import { useToast } from "../../Hooks/useToast";
import { formatDateTimeForDisplay } from "../../Utils/timeUtils";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import Typography from "@mui/material/Typography";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { Formik, Form, Field } from "formik"
import * as Yup from "yup";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import { Delete } from "@mui/icons-material";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import MainHeader from "../../Components/Headres/Main_Header";
import DatagridTable from "../../Components/Table/DataGrid_Table";
import { createColumn } from "../../Components/Table/DataGrid_Cullums";
import CellMultiImg from "../../Components/Table/Cell_MultiImg";
import AccoCodeModal from "./ACC_Code_Modal";
import SpecialReservationDialog from "../../Components/SpecialReservationDialog";

import { FetchEditReservations, DeleteReservation, FetchCreateReservations } from "../../Apis/Reservations/Reservations";
import { fetchReservations } from "../../Redux/Reservations_Slice";

const SearchSchema = Yup.object().shape({
  reservationNumber: Yup.string(),
  userName: Yup.string(),
  status: Yup.string(),
  packageId: Yup.string(),
  userId: Yup.string(),
  dateOfReservation: Yup.date().nullable(),
  isAGift: Yup.string().oneOf(["true", "false"], "Ungültiger Status"),
  payWay: Yup.string()
});

const ReservationsTable = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [openModal, setOpenModal] = useState(false);
  const { toastSuccess, toastError } = useToast();
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [Loading, setIsLoading] = useState(false);
  const [searchFilters, setSearchFilters] = useState({});
  const [modalType, setModalType] = useState("code");
  const [editingEmail, setEditingEmail] = useState(null);
  const [editEmailValue, setEditEmailValue] = useState("");
  const [specialReservationOpen, setSpecialReservationOpen] = useState(false);
  const [specialReservationLoading, setSpecialReservationLoading] = useState(false);
  
  // حالات التعديل
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingReservation, setEditingReservation] = useState(null);
  const [editFormData, setEditFormData] = useState({
    customerName: "",
    customerEmail: "",
    customerPhone: "",
    customerStreet: "",
    customerPLZ: "",
    customerCity: "",
    status: "",
    adminNote: "",
    note: ""
  });

  // حالات تأكيد الحذف
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reservationToDelete, setReservationToDelete] = useState(null);

  const { data, isLoading, error, pagination } = useSelector(
    (state) => state.Reservations
  );

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  useEffect(() => {
    dispatch(
      fetchReservations({
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
        filters: searchFilters
      })
    );
  }, [dispatch, paginationModel, searchFilters]);

  const handlePaginationModelChange = (newModel) => {
    setPaginationModel(newModel);
  };

  const handleImageClick = (reservation) => {
    setSelectedReservation(reservation);
    const type = reservation.payWay === "paypal" ? "paypal" : "code";
    setModalType(type);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedReservation(null);
  };

  const handleAccept = async () => {
    if (!selectedReservation?.id) {
      toastError("Keine Reservierung ausgewählt!");
      return;
    }

    setIsLoading(true);
    try {
      const response = await FetchEditReservations(
        { 
          isPaid: true,
          status: "accepted"
        },
        selectedReservation.id
      );
      
      if (response.status === 200 || response.status === 201) {
        toastSuccess("Reservierung erfolgreich angenommen!");
        handleCloseModal();
        dispatch(
          fetchReservations({
            page: paginationModel.page + 1,
            limit: paginationModel.pageSize,
            filters: searchFilters
          })
        );
      } else {
        toastError(`Fehler beim Annehmen: Status ${response.status}`);
      }
    } catch (error) {
      toastError("Fehler beim Annehmen der Reservierung. Bitte erneut versuchen."); 
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleDecline = async () => {
    if (!selectedReservation?.id) {
      toastError("Keine Reservierung ausgewählt!");
      return;
    }

    setIsLoading(true);
    try {
      const response = await FetchEditReservations(
        { 
          isCanceled: true,
          isPaid: false,
          status: "rejected"
        },
        selectedReservation.id
      );
      
      if (response.status === 200 || response.status === 201) {
        toastSuccess("Reservierung erfolgreich abgelehnt!");
        handleCloseModal();
        dispatch(
          fetchReservations({
            page: paginationModel.page + 1,
            limit: paginationModel.pageSize,
            filters: searchFilters
          })
        );
      } else {
        const errorMsg = response?.data?.message || response?.error || `Fehler beim Ablehnen: Status ${response.status}`;
        toastError(errorMsg);
      }
    } catch (error) {
      const serverMessage = error?.response?.data?.message || error?.message || "Fehler beim Ablehnen der Reservierung. Bitte erneut versuchen.";
      toastError(serverMessage); 
    } finally {
      setIsLoading(false);
    }
  };

  // دالة فتح مودال تأكيد الحذف
  const handleDeleteClick = (reservation) => {
    setReservationToDelete(reservation);
    setDeleteDialogOpen(true);
  };

  // دالة إغلاق مودال تأكيد الحذف
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setReservationToDelete(null);
  };

  // دالة الحذف الفعلية
  const handleConfirmDelete = async () => {
    if (!reservationToDelete) return;

    setIsLoading(true);
    try {
      const response = await DeleteReservation(reservationToDelete.id);
      
      if (response.status === 200) {
        toastSuccess("Reservierung erfolgreich gelöscht!");
        handleCloseDeleteDialog();
        dispatch(
          fetchReservations({
            page: paginationModel.page + 1,
            limit: paginationModel.pageSize,
            filters: searchFilters
          })
        );
      } else {
        const errorMsg = response?.data?.message || response?.error || `Fehler beim Löschen: Status ${response.status}`;
        toastError(errorMsg);
      }
    } catch (error) {
      const serverMessage = error?.response?.data?.message || error?.message || "Fehler beim Löschen der Reservierung. Bitte erneut versuchen.";
      toastError(serverMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // دوال التعديل
  const handleEdit = (reservation) => {
      setEditingReservation(reservation);
      setEditFormData({
          customerName: reservation.customerName || "",
          customerEmail: reservation.customerEmail || "",
          customerPhone: reservation.customerPhone || "",
          customerStreet: reservation.customerStreet || "",
          customerPLZ: reservation.customerPLZ || "",
          customerCity: reservation.customerCity || "",
          status: reservation.status || "",
          adminNote: reservation.adminNote || "",
          note: reservation.note || ""
      });
      setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
      setEditDialogOpen(false);
      setEditingReservation(null);
      setEditFormData({
          customerName: "",
          customerEmail: "",
          customerPhone: "",
          customerStreet: "",
          customerPLZ: "",
          customerCity: "",
          status: "",
          adminNote: "",
          note: ""
      });
  };

  const handleSaveEdit = async () => {
      if (!editingReservation) return;

      setIsLoading(true);
      try {
          const response = await FetchEditReservations(editFormData, editingReservation.id);
          
          if (response.status === 200 || response.status === 201) {
              toastSuccess("Reservierung erfolgreich aktualisiert!");
              handleCloseEditDialog();
              dispatch(
                  fetchReservations({
                      page: paginationModel.page + 1,
                      limit: paginationModel.pageSize,
                      filters: searchFilters
                  })
              );
          } else {
              const errorMsg = response?.data?.message || response?.error || `Fehler beim Aktualisieren: Status ${response.status}`;
              toastError(errorMsg);
          }
      } catch (error) {
          const serverMessage = error?.response?.data?.message || error?.message || "Fehler beim Aktualisieren der Reservierung. Bitte erneut versuchen.";
          toastError(serverMessage);
      } finally {
          setIsLoading(false);
      }
  };

  const handleFormChange = (field, value) => {
      setEditFormData(prev => ({
          ...prev,
          [field]: value
      }));
  };

  const handleSearch = (values) => {
    const filters = {};
    if (values.reservationNumber) filters.reservationNumber = values.reservationNumber;
    if (values.userName) filters.userName = values.userName;
    if (values.status) filters.status = values.status;
    if (values.packageId) filters.packageId = values.packageId;
    if (values.userId) filters.userId = values.userId;
    if (values.dateOfReservation) filters.dateOfReservation = values.dateOfReservation;
    if (values.isAGift) filters.isAGift = values.isAGift;
    if (values.payWay) filters.payWay = values.payWay;
    setSearchFilters(filters);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleReset = (resetForm) => {
    resetForm();
    setSearchFilters({});
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  // دوال تعديل الإيميل
  const handleEditEmail = (reservationId, currentEmail) => {
    setEditingEmail(reservationId);
    setEditEmailValue(currentEmail || "");
  };

  const handleCancelEditEmail = () => {
    setEditingEmail(null);
    setEditEmailValue("");
  };

  const handleSaveEmail = async (reservationId) => {
    if (!editEmailValue || !editEmailValue.includes('@')) {
      toastError("Bitte geben Sie eine gültige E-Mail-Adresse ein!");
      return;
    }

    setIsLoading(true);
    try {
      const response = await FetchEditReservations(
        { customerEmail: editEmailValue },
        reservationId
      );
      
      if (response.status === 200 || response.status === 201) {
        toastSuccess("E-Mail erfolgreich aktualisiert!");
        setEditingEmail(null);
        setEditEmailValue("");
        dispatch(
          fetchReservations({
            page: paginationModel.page + 1,
            limit: paginationModel.pageSize,
            filters: searchFilters
          })
        );
      } else {
        toastError(`Fehler beim Aktualisieren der E-Mail: ${response.status}`);
      }
    } catch (error) {
      toastError("Fehler beim Aktualisieren der E-Mail. Bitte erneut versuchen.");
    } finally {
      setIsLoading(false);
    }
  };

// دالة إنشاء الحجز المميز
const handleSpecialReservationSubmit = async (data) => {
  setSpecialReservationLoading(true);
  try {
    const formData = new FormData();
    
    // البيانات الأساسية للعميل
    formData.append('customerName', data.title);
    formData.append('customerEmail', 'special@reservation.com');
    formData.append('email', 'special@reservation.com');
    formData.append('customerPhone', '000000000');
    formData.append('customerStreet', 'Sonderreservierung');
    formData.append('customerPLZ', '00000');
    formData.append('customerCity', 'Special');
    
    // معلومات الباكج
    formData.append('packageName', 'Sonderreservierung');
    
    // تواريخ الحجز
    const startDateTime = new Date(
      data.date.getFullYear(),
      data.date.getMonth(),
      data.date.getDate(),
      data.startTime.getHours(),
      data.startTime.getMinutes()
    );
    
    const isoStringWithoutTimezone = new Date(
      startDateTime.getTime() - (startDateTime.getTimezoneOffset() * 60000)
    ).toISOString();
    
    formData.append('dateOfReservation', isoStringWithoutTimezone);
    
    // معلومات الحجز الأساسية
    formData.append('numberOfPersons', '1');
    formData.append('payWay', 'special');
    formData.append('note', data.description || 'Sonderreservierung');
    formData.append('isAGift', 'false');
    formData.append('selectedGender', '');
    formData.append('extraObjectsList', JSON.stringify([]));
    
    // إنشاء صورة وهمية للحجز المميز
    const canvas = document.createElement('canvas');
    canvas.width = 200;
    canvas.height = 100;
    const ctx = canvas.getContext('2d');
    
    ctx.fillStyle = '#ffd54f';
    ctx.fillRect(0, 0, 200, 100);
    
    ctx.fillStyle = '#333';
    ctx.font = 'bold 16px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('⭐ SPECIAL', 100, 35);
    ctx.fillText('RESERVATION', 100, 55);
    ctx.font = '12px Arial';
    ctx.fillText(data.title, 100, 75);
    
    canvas.toBlob((blob) => {
      const specialImage = new File([blob], 'special-reservation.png', { type: 'image/png' });
      formData.append('codePhotos', specialImage);
      
      sendSpecialReservation(formData);
    }, 'image/png');
    
  } catch (error) {
    console.log("❌ خطأ في إنشاء الحجز المميز:", error);
    toastError("Fehler beim Erstellen der Sonderreservierung. Bitte erneut versuchen.");
    setSpecialReservationLoading(false);
  }
};

// دالة إرسال الحجز المميز
const sendSpecialReservation = async (formData) => {
  try {
    const response = await FetchCreateReservations(formData);
    
    if (response.status === 201) {
      console.log("✅ تم إنشاء الحجز المميز بنجاح");
      toastSuccess("Sonderreservierung erfolgreich erstellt!");
      setSpecialReservationOpen(false);
      
      // إعادة تحميل البيانات
      dispatch(
        fetchReservations({
          page: paginationModel.page + 1,
          limit: paginationModel.pageSize,
          filters: searchFilters
        })
      );
    } else {
      console.log("❌ فشل في إنشاء الحجز المميز - Status:", response.status);
      const errorMsg = response?.data?.message || response?.error || `Fehler beim Erstellen: Status ${response.status}`;
      toastError(errorMsg);
    }
    
  } catch (error) {
    console.log("❌ خطأ في API الحجز المميز:", error);
    toastError("Fehler beim Erstellen der Sonderreservierung. Bitte erneut versuchen.");
  } finally {
    setSpecialReservationLoading(false);
  }
};

  const columns = [
    {
      ...createColumn("reservationNumber", "Buchungsnummer", 150),
      renderCell: (params) => {
        const isSpecial = params.row.isSpecial || params.row.payWay === 'special';
        return (
          <Button
            onClick={() => navigate(`ViewReservations/${params.row.id}`)}
            sx={{
              padding: 0,
              textTransform: "none",
              color: isSpecial ? "#ff8f00" : "#CD9D41",
              fontWeight: "bold",
              backgroundColor: isSpecial ? "#fff3e0" : "transparent",
              borderRadius: 1,
              px: 1
            }}
          >
            {isSpecial && "⭐ "}{params.value}
          </Button>
        );
      },
    },
    {
      ...createColumn("customerName", "Kundenname", 200),
      renderCell: (params) => {
        const isSpecial = params.row.isSpecial || params.row.payWay === 'special';
        return (
          <span style={{
            fontWeight: "bold",
            color: isSpecial ? "#ff8f00" : "#333",
            backgroundColor: isSpecial ? "#fff8e1" : "transparent",
            padding: isSpecial ? "4px 8px" : "0",
            borderRadius: isSpecial ? "4px" : "0",
            display: "inline-block"
          }}>
            {isSpecial && "⭐ "}{params.value || params.row.userName || "N/A"}
          </span>
        );
      },
    },
    {
      ...createColumn("customerEmail", "E-Mail", 280),
      renderCell: (params) => {
        const isEditing = editingEmail === params.row.id;
        const emailValue = params.value || params.row.email || "";
        
        if (isEditing) {
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
              <TextField
                value={editEmailValue}
                onChange={(e) => setEditEmailValue(e.target.value)}
                size="small"
                variant="outlined"
                type="email"
                placeholder="E-Mail eingeben"
                sx={{ flex: 1, minWidth: 150 }}
                autoFocus
              />
              <IconButton
                size="small"
                onClick={() => handleSaveEmail(params.row.id)}
                sx={{ color: "#4CAF50" }}
                disabled={Loading}
              >
                <SaveIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={handleCancelEditEmail}
                sx={{ color: "#f44336" }}
                disabled={Loading}
              >
                <CancelIcon fontSize="small" />
              </IconButton>
            </Box>
          );
        }
        
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
            <span 
              style={{ 
                flex: 1, 
                color: emailValue ? "#333" : "#999",
                fontStyle: emailValue ? "normal" : "italic"
              }}
            >
              {emailValue || "Keine E-Mail"}
            </span>
            <IconButton
              size="small"
              onClick={() => handleEditEmail(params.row.id, emailValue)}
              sx={{ color: "#CD9D41" }}
              title="E-Mail bearbeiten"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Box>
        );
      },
    },
    createColumn("customerPhone", "Telefon", 150),
    createColumn("customerStreet", "Straße und HausNr", 200),
    createColumn("customerPLZ", "PLZ", 100),
    createColumn("customerCity", "Stadt", 150),
    {
      ...createColumn("createdAt", "Erstellt am", 180),
      renderCell: (params) => {
        if (!params.value) return "N/A";
        const date = new Date(params.value);
        
        // استخدام التوقيت المحلي بدلاً من UTC لإصلاح مشكلة نقص الساعات
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        
        return (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <Typography variant="body2" sx={{ fontWeight: "bold", color: "#CD9D41" }}>
              {hours}:{minutes}
            </Typography>
            <Typography variant="caption" sx={{ color: "#666", fontSize: "0.75rem" }}>
              {day}.{month}.{year}
            </Typography>
          </Box>
        );
      }
    },
    {
      ...createColumn("dateOfReservation", "Datum der Reservierung", 200),
      renderCell: (params) => formatDateTimeForDisplay(params.value)
    },
    {
      ...createColumn("dateEndOfReservation", "Datum Ende der Reservierung", 250),
      renderCell: (params) => formatDateTimeForDisplay(params.value),
    },
    {
      ...createColumn("payWay", "Zahlungsart", 180),
      renderCell: (params) => {
        const isSpecial = params.row.isSpecial || params.row.payWay === 'special';
        let displayValue;
        
        if (params.value === 'paypal') {
          displayValue = 'PayPal';
        } else if (isSpecial) {
          displayValue = 'Spezial';
        } else {
          displayValue = 'Code';
        }
        
        return (
          <span style={{
            fontWeight: "bold",
            color: isSpecial ? "#ff8f00" : (params.value === 'paypal' ? "#4CAF50" : "#333"),
            backgroundColor: isSpecial ? "#fff3e0" : "transparent",
            padding: isSpecial ? "4px 8px" : "0",
            borderRadius: isSpecial ? "4px" : "0",
            display: "inline-block"
          }}>
            {displayValue}
          </span>
        );
      },
    },
    createColumn("adminNote", "adminNotiz", 250),
    createColumn("note", "Notiz", 250),
    {
      ...createColumn("isAGift", "Geschenk", 120),
      renderCell: (params) => {
        if (params.value) {
          return (
            <span
              style={{
                backgroundColor: "#FFE4E1",
                color: "#D2691E",
                padding: "8px 12px",
                borderRadius: "8px",
                fontWeight: "bold",
                fontSize: "12px",
                border: "1px solid #D2691E"
              }}
            >
              🎁 Geschenk
            </span>
          );
        }
        return (
          <span
            style={{
              color: "#999",
              fontSize: "12px"
            }}
          >
            -
          </span>
        );
      },
    },
    createColumn("status", "Status", 200, "string", ({ value, row }) => {
      const isSpecial = row.isSpecial || row.payWay === 'special';
      let color, label;
      
      if (isSpecial) {
        color = "#fff3e0";
        label = "⭐ Sonderreservierung";
      } else if (value === "accepted" || value === "Angenommen") {
        color = "#B6E6C1";
        label = "Angenommen";
      } else if (value === "rejected" || value === "Abgelehnt") {
        color = "#E6B6B6";
        label = "Abgelehnt";
      } else {
        color = "#F0E0C0";
        label = "Ausstehend";
      }
      
      return (
        <span
          style={{
            backgroundColor: color,
            padding: "12px 18px",
            borderRadius: "10px",
            fontWeight: "bold",
            color: isSpecial ? "#ff8f00" : "inherit",
            border: isSpecial ? "2px solid #ffd54f" : "none"
          }}
        >
          {label}
        </span>
      );
    }),
    createColumn("codePhoto", "Bild", 100, "actions", (params) => (
      <CellMultiImg
        images={params.row.codePhotos}
        codePhoto={params.row.codePhoto}
        paymentProofPhoto={params.row.paymentProofPhoto}
        payWay={params.row.payWay}
        onClick={(imageData) => {
          handleImageClick({
            ...params.row,
            imageData
          });
        }}
        width="80px"
        height="60px"
      />
    )),
    {
      ...createColumn("actions", "Aktionen", 150),
      sortable: false,
      renderCell: (params) => {
        return (
          <Box sx={{ display: "flex", gap: 1 }}>
            <IconButton 
              onClick={() => handleEdit(params.row)} 
              color="primary"
              disabled={Loading}
              title="Reservierung bearbeiten"
              sx={{
                '&:hover': {
                  backgroundColor: '#e3f2fd',
                }
              }}
            >
              <EditIcon />
            </IconButton>
            <IconButton 
              onClick={() => handleDeleteClick(params.row)} 
              color="error"
              disabled={Loading}
              title="Reservierung löschen"
              sx={{
                '&:hover': {
                  backgroundColor: '#ffebee',
                }
              }}
            >
              <Delete />
            </IconButton>
          </Box>
        );
      },
    }
  ];

  const Toolbar = () => (
    <Box sx={{ p: 2, bgcolor: "#D6B981" }}>
      <Formik
        initialValues={{ 
          reservationNumber: "", 
          userName: "", 
          status: "", 
          packageId: "", 
          userId: "", 
          dateOfReservation: null, 
          isAGift: "", 
          payWay: "" 
        }}
        validationSchema={SearchSchema}
        onSubmit={handleSearch}
      >
        {({ errors, touched, resetForm, setFieldValue, values, setErrors }) => (
          <Form style={{ display: "flex", alignItems: "center", gap: 20, width: "100%", justifyContent: "end" }}>
            <Field
              as={TextField}
              name="reservationNumber"
              label="Buchungsnummer"
              variant="outlined"
              size="small"
              error={touched.reservationNumber && Boolean(errors.reservationNumber)}
              helperText={touched.reservationNumber && errors.reservationNumber}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
            <Field
              as={TextField}
              name="userName"
              label="Kundenname"
              variant="outlined"
              size="small"
              error={touched.userName && Boolean(errors.userName)}
              helperText={touched.userName && errors.userName}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
            <Field
              as={TextField}
              select
              name="status"
              label="Status"
              variant="outlined"
              size="small"
              error={touched.status && Boolean(errors.status)}
              helperText={touched.status && errors.status}
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="accepted">Angenommen</MenuItem>
              <MenuItem value="rejected">Abgelehnt</MenuItem>
              <MenuItem value="pending">Ausstehend</MenuItem>
            </Field>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              slotProps={{ textField: { size: 'small' } }}      
              disableTime
              label="Reservierungsdatum"
              value={values.dateOfReservation}
              ampm={false}
              format="dd.MM.yyyy"
              onChange={(newValue) => {
                if (newValue) {
                  const normalizedDate = new Date(newValue);
                  normalizedDate.setHours(6, 0, 0, 0); 
                  setFieldValue("dateOfReservation", normalizedDate);
                } else {
                  setFieldValue("dateOfReservation", null);
                }
                if (errors.dateOfReservation) {
                  setErrors({ ...errors, dateOfReservation: undefined });
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  sx={{height:10}}
                  error={touched.dateOfReservation && Boolean(errors.dateOfReservation)}
                  helperText={touched.dateOfReservation && errors.dateOfReservation}
                />
              )}
            />
            </LocalizationProvider>
            <Button  type="submit" variant="contained" color="inherit" size="medium">
              Suchen
            </Button>
            <Button
            sx={{fontSize:"12px",px:3}}
              type="button"
              variant="outlined"
              color="inherit"
              size="medium"
              onClick={() => handleReset(resetForm)}
            >
              Aktualisieren
            </Button>
          </Form>
        )}
      </Formik>
    </Box>
  );

  // مودال التعديل
  const EditDialog = () => (
    <Dialog 
      open={editDialogOpen} 
      onClose={handleCloseEditDialog}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        Reservierung bearbeiten Nr.: {editingReservation?.reservationNumber}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Kundenname"
              value={editFormData.customerName}
              onChange={(e) => handleFormChange('customerName', e.target.value)}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="E-Mail"
              type="email"
              value={editFormData.customerEmail}
              onChange={(e) => handleFormChange('customerEmail', e.target.value)}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Telefonnummer"
              value={editFormData.customerPhone}
              onChange={(e) => handleFormChange('customerPhone', e.target.value)}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Straße und Hausnummer"
              value={editFormData.customerStreet}
              onChange={(e) => handleFormChange('customerStreet', e.target.value)}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Postleitzahl"
              value={editFormData.customerPLZ}
              onChange={(e) => handleFormChange('customerPLZ', e.target.value)}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Stadt"
              value={editFormData.customerCity}
              onChange={(e) => handleFormChange('customerCity', e.target.value)}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={editFormData.status}
                label="Status"
                onChange={(e) => handleFormChange('status', e.target.value)}
              >
                <MenuItem value="pending">Ausstehend</MenuItem>
                <MenuItem value="accepted">Angenommen</MenuItem>
                <MenuItem value="rejected">Abgelehnt</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Admin-Notiz"
              multiline
              rows={3}
              value={editFormData.adminNote}
              onChange={(e) => handleFormChange('adminNote', e.target.value)}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Kunden-Notiz"
              multiline
              rows={3}
              value={editFormData.note}
              onChange={(e) => handleFormChange('note', e.target.value)}
              variant="outlined"
              size="small"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseEditDialog} color="inherit">
          Abbrechen
        </Button>
        <Button 
          onClick={handleSaveEdit} 
          variant="contained" 
          color="primary"
          disabled={Loading}
        >
          Änderungen speichern
        </Button>
      </DialogActions>
    </Dialog>
  );

  // مودال تأكيد الحذف الجميل
  const DeleteConfirmDialog = () => (
    <Dialog 
      open={deleteDialogOpen} 
      onClose={handleCloseDeleteDialog}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 10px 40px rgba(0,0,0,0.1)'
        }
      }}
    >
      <DialogContent sx={{ textAlign: 'center', py: 4 }}>
        <Box sx={{ mb: 3 }}>
          <WarningAmberIcon 
            sx={{ 
              fontSize: 80, 
              color: '#ff6b6b',
              mb: 2
            }} 
          />
        </Box>
        
        <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2, color: '#333' }}>
          Reservierung löschen?
        </Typography>
        
        <Typography variant="body1" sx={{ mb: 3, color: '#666', lineHeight: 1.6 }}>
          Sind Sie sicher, dass Sie die Reservierung 
          <strong style={{ color: '#CD9D41' }}> {reservationToDelete?.reservationNumber} </strong>
          von <strong>{reservationToDelete?.customerName || reservationToDelete?.userName}</strong> löschen möchten?
        </Typography>
        
        <Box sx={{ 
          backgroundColor: '#fff3cd', 
          border: '1px solid #ffeaa7',
          borderRadius: 2,
          p: 2,
          mb: 3
        }}>
          <Typography variant="body2" sx={{ color: '#856404', fontWeight: 'medium' }}>
            ⚠️ Diese Aktion kann nicht rückgängig gemacht werden!
          </Typography>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ justifyContent: 'center', pb: 3, gap: 2 }}>
        <Button 
          onClick={handleCloseDeleteDialog}
          variant="outlined"
          size="large"
          sx={{ 
            minWidth: 120,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 'medium'
          }}
        >
          Abbrechen
        </Button>
        <Button 
          onClick={handleConfirmDelete}
          variant="contained"
          color="error"
          size="large"
          disabled={Loading}
          sx={{ 
            minWidth: 120,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 'medium',
            backgroundColor: '#ff6b6b',
            '&:hover': {
              backgroundColor: '#ff5252'
            }
          }}
        >
          {Loading ? 'Wird gelöscht...' : 'Ja, löschen'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Box>
      <MainHeader
        onNavigate={() => navigate("AddBuchungen")}
        title="Neue Buchung"
        secondaryAction={{
          label: "⭐ Sonderreservierung",
          onClick: () => setSpecialReservationOpen(true),
          color: "warning"
        }}
      />
      <Box sx={{ height: 600, width: "100%", mt: 3 }}>
        <DatagridTable
          columns={columns}
          rows={Array.isArray(data) ? data : []}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          rowCount={pagination?.total || 0}
          pageSizeOptions={[5, 10, 25]}
          loading={isLoading}
          toolbarComponent={<Toolbar />}
        />
      </Box>
      
      <AccoCodeModal
        open={openModal}
        onClose={handleCloseModal}
        reservation={selectedReservation}
        onAccept={handleAccept}
        onDecline={handleDecline}
        isLoading={Loading}
        modalType={modalType}
      />
      
      <SpecialReservationDialog
        open={specialReservationOpen}
        onClose={() => setSpecialReservationOpen(false)}
        onSubmit={handleSpecialReservationSubmit}
        loading={specialReservationLoading}
      />

      <EditDialog />
      <DeleteConfirmDialog />
    </Box>
  );
};

export default ReservationsTable;